<template>
  <!-- ===================================================================
       GALERIE — écran de l'application, balisage propre.
       Le site garde le sien, intact, dans le `v-else` plus bas.

       Chargement : les photos sont servies dans leur définition d'origine,
       sans variante réduite côté serveur (vérifié : toutes les tailles
       intermédiaires répondent 404). Elles pèsent de 0,3 à 8,9 Mo, soit
       environ 37 Mo pour les 18 photos. D'où `loading="lazy"` sur toutes et
       un rapport de forme réservé avant l'arrivée du fichier : seules les
       tuiles réellement à l'écran sont téléchargées, et rien ne saute.

       Mise en colonnes : les photos alternent portrait et paysage (7 contre 3
       sur un échantillon de 10). On respecte donc leur rapport naturel plutôt
       que de les forcer dans un gabarit — aucune n'est recadrée.
       =================================================================== -->
  <div v-if="isNative" class="ga-page">

    <header class="ga-top">
      <div class="ga-top__text">
        <h1 class="ga-top__title">Galerie</h1>
        <p class="ga-top__sub">Les sorties OuiCSport en images</p>
      </div>
      <span v-if="images.length" class="ga-top__count">{{ images.length }}</span>
    </header>

    <div v-if="loading" class="ga-grid" aria-hidden="true">
      <span v-for="n in 6" :key="n" class="ga-tile ga-tile--ghost" :style="{ aspectRatio: n % 3 === 0 ? '4 / 3' : '3 / 4' }"></span>
    </div>

    <div v-else-if="images.length" class="ga-grid">
      <button
        v-for="(image, i) in images"
        :key="image.src"
        type="button"
        class="ga-tile"
        :class="{ 'is-ready': gaCharge[i], 'is-ko': gaErreur[i] }"
        :data-ga-index="i"
        :aria-label="`Ouvrir la photo ${i + 1} sur ${images.length}`"
        @click="gaOuvrir(i)"
      >
        <img
          v-if="gaVisible[i]"
          :src="image.src"
          alt=""
          decoding="async"
          @load="gaCharge[i] = true"
          @error="gaErreur[i] = true"
        >
        <span v-if="gaErreur[i]" class="ga-tile__ko">Photo indisponible</span>
      </button>
    </div>

    <div v-else class="ga-void">
      <h2 class="ga-void__title">Galerie vide</h2>
      <p class="ga-void__text">Les photos des prochaines sorties apparaîtront ici.</p>
    </div>

    <!-- Visionneuse plein écran -->
    <div
      v-if="gaIndex !== null"
      class="ga-view"
      data-oc-dismissable="true"
      @touchstart.passive="gaToucheDebut"
      @touchend.passive="gaToucheFin"
    >
      <div class="ga-view__bar">
        <span class="ga-view__count">{{ gaIndex + 1 }} / {{ images.length }}</span>
        <button type="button" class="ga-view__close" data-oc-dismiss="true" aria-label="Fermer" @click="gaFermer">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div class="ga-view__stage">
        <button v-if="images.length > 1" type="button" class="ga-view__step ga-view__step--prev" aria-label="Photo précédente" @click="gaPas(-1)">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </button>
        <img class="ga-view__img" :src="images[gaIndex].src" alt="" decoding="async">
        <button v-if="images.length > 1" type="button" class="ga-view__step ga-view__step--next" aria-label="Photo suivante" @click="gaPas(1)">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </button>
      </div>
    </div>
  </div>

  <!-- ===== Site web : balisage d'origine, strictement inchangé ===== -->
  <template v-else>

  <div v-if="loading" class="loading-overlay">
    <div class="spinner"></div>
  </div>
  <section class="hero2">
    </section>

    <section class="gallery">
        <h2>GALERIE</h2>

        <div class="gallery-grid">
            <img
                v-for="image in images"
                :key="image.src"
                :src="image.src"
                :alt="image.alt"
                @click="openModal(image)"
            >
        </div>
    </section>

    <div
      v-if="showModal"
      class="modal"
      @click="closeModal"
    >
  <span
    class="close"
  >&times;</span>
  <img :src="selectedImageSrc" :alt="selectedImageAlt" class="modal-content" id="img01">
  <div id="caption" class="">{{ selectedImageAlt }}</div>
</div>
  </template>
</template>
  
  <script>
  import axios from 'axios';
  import { isNative } from '@/api/base';
  export default {
    data() {
      return {
        isNative,
        info: [],
        files: [],
        images: [],
        showModal: false,
        loading: true,
        gaIndex: null,
        gaCharge: {},
        gaVisible: {},
        gaObs: null,
        gaErreur: {},
        gaX: 0,
      selectedImageSrc: '',
      selectedImageAlt: '', 
      };
    },
    beforeUnmount() {
      if (this.gaObs) this.gaObs.disconnect();
    },
    mounted() {
      this.fetchImages();
  

    },
    methods: {
    async fetchImages() {
      try {
        const response = await axios.get('/api/gallery/images');
        this.images = response.data
            .filter(filename => !filename.startsWith('banner_'))
            .map(filename => ({
              src: `https://www.ouicsport.fr/api/gallery_img/${filename}`,
              alt: `${filename}`
            }));
        this.loading = false;
        if (this.isNative) this.$nextTick(this.gaObserver);
      } catch (error) {
        console.error("Erreur lors de la récupération des images :", error);
        this.loading = false; 
      }
    },

    /**
     * Ne charge une photo que lorsque sa tuile approche de l'écran.
     *
     * `loading="lazy"` ne suffisait pas : sur une page de 2 100px, le seuil de
     * préchargement du navigateur couvre tout le document, et les dix-huit
     * photos partaient d'un coup — 32,8 Mo mesurés. Avec un observateur, on
     * décide nous-mêmes : 200px d'avance, pas plus.
     */
    gaObserver() {
      if (!('IntersectionObserver' in window)) {
        // Sans observateur, on retombe sur un chargement immédiat plutôt que
        // sur une galerie vide.
        this.images.forEach((_, i) => { this.gaVisible[i] = true; });
        return;
      }
      if (this.gaObs) this.gaObs.disconnect();
      this.gaObs = new IntersectionObserver((entrees) => {
        for (const e of entrees) {
          if (!e.isIntersecting) continue;
          const i = Number(e.target.dataset.gaIndex);
          if (!Number.isNaN(i)) this.gaVisible[i] = true;
          this.gaObs.unobserve(e.target);
        }
      }, { rootMargin: '200px 0px' });
      document.querySelectorAll('.ga-tile[data-ga-index]').forEach((el) => this.gaObs.observe(el));
    },
    openModal(image) {
      this.showModal = true;
      this.selectedImageSrc = image.src;
      this.selectedImageAlt = image.alt;
    },
    closeModal() {
      this.showModal = false;
    },

    /* ---- Visionneuse de l'application ---- */
    gaOuvrir(i) { this.gaIndex = i; },
    gaFermer() { this.gaIndex = null; },
    gaPas(d) {
      const n = this.images.length;
      if (!n) return;
      this.gaIndex = (this.gaIndex + d + n) % n;
    },
    gaToucheDebut(e) { this.gaX = e.touches[0].clientX; },
    gaToucheFin(e) {
      // 40px de course minimum : en dessous, c'est une pression, pas un geste.
      const d = this.gaX - e.changedTouches[0].clientX;
      if (Math.abs(d) > 40) this.gaPas(d > 0 ? 1 : -1);
    }
  }

  };
 
  </script>

  <style scoped>
  /* Vos styles CSS iront ici */
  </style>

<style>
/* =========================================================================
   GALERIE — feuille de l'application, écrite de zéro
   =========================================================================
   Balisage natif distinct de celui du site : rien n'est hérité ni combattu.
   Sélecteurs préfixés `body.is-native` pour que le contrôle automatique
   vérifie que le site reste intact.
   ========================================================================= */

body.is-native .ga-page,
body.is-native .ga-page *,
body.is-native .ga-page *::before,
body.is-native .ga-page *::after {
  box-sizing: border-box;
}

body.is-native .ga-page header,
body.is-native .ga-page h1,
body.is-native .ga-page h2,
body.is-native .ga-page p,
body.is-native .ga-page span,
body.is-native .ga-page div,
body.is-native .ga-page button,
body.is-native .ga-page img {
  all: unset;
  display: revert;
  /* `all: unset` retire le `overflow: clip` que le navigateur applique
     aux éléments remplacés : sans lui, le débordement d'`object-fit:
     cover` se peint HORS du cadre de l'image. */
  overflow: clip;
  box-sizing: border-box;
}

body.is-native .ga-page {
  --ga-bg: #F5F0E6;
  --ga-surface: #EFE8DA;
  --ga-raised: #E5DDD0;
  --ga-dark: #2B231F;
  --ga-ink: #28201C;
  --ga-ink-2: #6B5E54;
  --ga-ink-3: #8C7E73;
  --ga-on-dark: #FAF6EE;
  --ga-red: #DD4124;
  --ga-hair: rgba(107, 94, 84, 0.14);

  min-height: 100%;
  padding-top: calc(env(safe-area-inset-top, 0px) + 14px);
  padding-bottom: var(--oc-sp-6);
  background: var(--ga-bg);
  font-family: 'Dosis', system-ui, sans-serif;
  color: var(--ga-ink);
  -webkit-user-select: none;
  user-select: none;
}

/* ---------- Titre ---------- */

body.is-native .ga-page .ga-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 0 16px 14px;
}

body.is-native .ga-page .ga-top__title {
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 30px;
  line-height: 1;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--ga-ink);
}

body.is-native .ga-page .ga-top__sub {
  margin-top: 4px;
  font-size: 15px;
  font-weight: 600;
  color: var(--ga-ink-2);
}

body.is-native .ga-page .ga-top__count {
  flex: 0 0 auto;
  padding: 4px 11px;
  font-size: 14px;
  font-weight: 800;
  color: var(--ga-on-dark);
  background: var(--ga-dark);
  border-radius: 12px;
  font-variant-numeric: tabular-nums;
}

/* ---------- Mise en colonnes ----------
   `column-count` plutôt qu'une grille : chaque photo garde son rapport
   naturel, portraits et paysages s'imbriquent sans être recadrés. */

body.is-native .ga-page .ga-grid {
  column-count: 2;
  column-gap: 10px;
  padding: 0 14px;
}

body.is-native .ga-page .ga-tile {
  position: relative;
  display: block;
  width: 100%;
  margin: 0 0 10px;
  overflow: hidden;
  background: var(--ga-raised);
  border-radius: 16px;
  cursor: pointer;
  /* Empêche une photo d'être coupée entre le bas d'une colonne et le haut
     de la suivante. */
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  transition: transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ga-page .ga-tile:active { transform: scale(0.97); }

/* Hauteur réservée AVANT l'arrivée de la photo.
   Sans elle, `loading="lazy"` ne sert à rien : une tuile vide mesure 0px de
   haut, les dix-huit s'empilent donc au sommet de l'écran, le navigateur les
   considère toutes comme visibles et télécharge tout — 32,8 Mo mesurés au
   premier affichage. Avec un rapport de forme provisoire, seules les tuiles
   réellement à l'écran sont chargées.
   3/4 correspond à l'orientation majoritaire du fonds (7 portraits sur 10) ;
   dès la photo arrivée, on rend la main à son rapport réel. */
body.is-native .ga-page .ga-tile:not(.is-ready):not(.is-ko) {
  aspect-ratio: 3 / 4;
}

body.is-native .ga-page .ga-tile.is-ready {
  aspect-ratio: auto;
}

body.is-native .ga-page .ga-tile img {
  display: block;
  width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

body.is-native .ga-page .ga-tile.is-ready img { opacity: 1; }

/* Une photo qui n'arrive pas ne doit pas laisser un rectangle muet. */
body.is-native .ga-page .ga-tile.is-ko {
  aspect-ratio: 4 / 3;
  background: var(--ga-surface);
  border: 1px solid var(--ga-hair);
}

body.is-native .ga-page .ga-tile__ko {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
  color: var(--ga-ink-3);
}

/* Squelette pendant l'appel réseau. */
body.is-native .ga-page .ga-tile--ghost {
  background: linear-gradient(90deg, var(--ga-surface) 25%, var(--ga-raised) 37%, var(--ga-surface) 63%);
  background-size: 400% 100%;
  animation: ga-shimmer 1.4s ease infinite;
}

@keyframes ga-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

body.is-native .ga-page .ga-void {
  padding: 56px 24px;
  text-align: center;
}

body.is-native .ga-page .ga-void__title {
  margin-bottom: 8px;
  font-family: 'Gagalin', 'Dosis', sans-serif;
  font-size: 22px;
  text-transform: uppercase;
  color: var(--ga-ink);
}

body.is-native .ga-page .ga-void__text {
  font-size: 16px;
  font-weight: 600;
  color: var(--ga-ink-2);
}

/* ---------- Visionneuse plein écran ---------- */

body.is-native .ga-page .ga-view {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  /* Fond opaque, et non translucide : la grille et la barre d'onglets
     transparaissaient derrière la photo, ce qui parasitait la lecture. Une
     visionneuse doit isoler l'image. */
  background: #141110;
  animation: ga-apparait 200ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

@keyframes ga-apparait {
  from { opacity: 0; }
  to { opacity: 1; }
}

body.is-native .ga-page .ga-view__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: calc(env(safe-area-inset-top, 0px) + 10px) 16px 10px;
}

body.is-native .ga-page .ga-view__count {
  font-size: 15px;
  font-weight: 800;
  color: var(--ga-on-dark);
  font-variant-numeric: tabular-nums;
}

body.is-native .ga-page .ga-view__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--ga-on-dark);
  background: rgba(250, 246, 238, 0.15);
  border-radius: 22px;
  cursor: pointer;
}

body.is-native .ga-page .ga-view__stage {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 8px 16px calc(env(safe-area-inset-bottom, 0px) + 20px);
}

body.is-native .ga-page .ga-view__img {
  display: block;
  max-width: 100%;
  max-height: 100%;
  /* `contain` : on ne recadre jamais une photo qu'on affiche en grand. */
  object-fit: contain;
  border-radius: 14px;
}

body.is-native .ga-page .ga-view__step {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: var(--ga-on-dark);
  background: rgba(43, 35, 31, 0.86);
  border: 1px solid rgba(250, 246, 238, 0.2);
  border-radius: 22px;
  cursor: pointer;
}

body.is-native .ga-page .ga-view__step--prev { left: 10px; }
body.is-native .ga-page .ga-view__step--next { right: 10px; }

@media (prefers-reduced-motion: reduce) {
  body.is-native .ga-page .ga-tile--ghost,
  body.is-native .ga-page .ga-view { animation: none; }
  body.is-native .ga-page .ga-tile img { transition: none; }
}
</style>

  